import { useBoolean, useRequest } from 'ahooks';
import { useCompany } from '../hooks/company';
import {
  AuditUserCallFileDTO,
  CustomerEventTypeEnum,
  CustomerPoolEnum,
  EventDTO,
  auditController,
  customerController,
} from '../api';
import { Button, Drawer, Space, Spin, Tag, Timeline } from 'antd';
import { customerEventMapping } from '../constants';
import UserInfoModal from './UserInfoModal';
import CallFilePlayer from './CallFilePlayer';
import dayjs from 'dayjs';
import '../styles/common.scss';
import { useEffect, useMemo, useState } from 'react';
import { getPoolType } from '../utils';

/** 时间线条目：跟进事件，或混入的通话录音（callFile 非空时） */
export type TimelineItemData = Omit<EventDTO, 'type' | 'userId'> & {
  type?: CustomerEventTypeEnum;
  userId?: EventDTO['userId'];
  callFile?: AuditUserCallFileDTO;
};

/**
 * 拉取跟进事件 + 通话记录 + 通话录音文件，按 createdAt 倒序合并成时间线数据
 * 录音数据范围由后端按客户实际池子和当前用户权限过滤
 */
const fetchTimelineItems = async (
  companyId: I.ID,
  customerId: I.ID,
  blacklistId?: I.ID,
): Promise<TimelineItemData[]> => {
  const [events, callLogs, callFiles] = await Promise.all([
    customerController.listEvent(companyId, customerId, blacklistId),
    customerController
      .getCustomerCallLogs(companyId, customerId, blacklistId)
      .catch(() => [] as EventDTO[]),
    auditController
      .getUserCallFilesByCustomerId(companyId, customerId, blacklistId)
      .catch(() => [] as AuditUserCallFileDTO[]),
  ]);

  // 只混入有 OSS key（可播放）的录音，时间用 createdAt；防御非数组响应
  const callFileItems: TimelineItemData[] = (
    Array.isArray(callFiles) ? callFiles : []
  )
    .filter((file) => !!file.key)
    .map((file) => ({
      id: file.id,
      customerId,
      userId: file.user?.id,
      userName: file.user?.name || '',
      createdAt: file.createdAt,
      updatedAt: file.updatedAt,
      callFile: file,
    }));

  return [...events, ...callLogs, ...callFileItems].sort((a, b) =>
    (a?.createdAt || '') > (b?.createdAt || '') ? -1 : 1,
  );
};

export const EventTimelineAutoLoad = ({
  customerId,
  companyId,
  customerName,
  isRe,
  pool,
}: {
  customerId: I.ID;
  companyId: I.ID;
  customerName: string;
  isRe: boolean;
  pool: CustomerPoolEnum;
}) => {
  const { hasPermission } = useCompany();

  const showReEvents = useMemo(() => {
    return hasPermission(
      `page:customer-list:${getPoolType(pool)}-pool:show-re-events`,
    );
  }, [hasPermission, pool]);

  const [open, setOpen] = useState(false);
  const { data, loading, run } = useRequest(
    () => fetchTimelineItems(companyId, customerId),
    {
      refreshDeps: [customerId],
    },
  );

  const events = useMemo(() => {
    const sortedEvent = data?.sort((a, b) =>
      (a?.createdAt || '') > (b?.createdAt || '') ? -1 : 1,
    );

    if (!isRe || showReEvents) {
      return sortedEvent;
    }

    const reEvent = sortedEvent?.find(
      (item) => item.type === CustomerEventTypeEnum.REDUPLICATE,
    );

    if (reEvent) {
      return sortedEvent?.filter((item) => item.createdAt > reEvent.createdAt);
    }

    return sortedEvent;
  }, [data, isRe, showReEvents]);

  if (events?.length === 0) {
    return null;
  }

  return (
    <Space direction="vertical">
      <Button type="link" onClick={() => setOpen(true)}>
        展开
      </Button>
      <EventTimeline events={events} loading={loading} />
      <LastEventDrawer
        pool={pool}
        customerId={customerId}
        customerName={customerName}
        open={open}
        onCancel={() => setOpen(false)}
      />
    </Space>
  );
};

export const EventTimeline = ({
  events,
  loading,
}: {
  events?: TimelineItemData[];
  loading: boolean;
}) => {
  const { config: companyConfig } = useCompany();

  return (
    <Spin spinning={loading}>
      <Timeline
        items={events
          ?.filter((item) => {
            if (
              companyConfig?.hideReport &&
              item.type &&
              [
                CustomerEventTypeEnum.LOAN,
                CustomerEventTypeEnum.VISIT,
              ].includes(item.type)
            ) {
              return false;
            }
            return true;
          })
          .map((item) => ({
            children: (
              <Space direction="vertical">
                <span>
                  {item.userId && (
                    <UserInfoModal id={item.userId} name={item.userName} />
                  )}
                  {item.callFile ? (
                    <Tag color="blue">通话录音</Tag>
                  ) : (
                    <Tag>{customerEventMapping[item.type!]}</Tag>
                  )}
                  <span>
                    {dayjs(item?.createdAt).format('YYYY-MM-DD HH:mm:ss')}
                  </span>
                </span>
                {item.callFile ? (
                  <CallFilePlayer
                    ossKey={item.callFile.key!}
                    duration={item.callFile.duration}
                  />
                ) : (
                  <span
                    style={{
                      whiteSpace: 'pre-line',
                      fontWeight: '500',
                    }}
                  >
                    {item?.comment}
                  </span>
                )}
                {/* <span>{item?.remark}</span> */}
              </Space>
            ),
          }))}
      />
    </Spin>
  );
};

export const LastEventDrawer = ({
  customerId,
  customerName,
  open,
  pool,
  onCancel,
  blacklistId,
}: {
  customerId: I.ID;
  customerName: string;
  open?: boolean;
  onCancel: () => void;
  blacklistId?: I.ID;
  pool: CustomerPoolEnum;
}) => {
  const { id: companyId, hasPermission } = useCompany();

  // 抽屉打开时才拉取，避免关闭状态下的重复请求
  const { data, loading, run } = useRequest(
    () => fetchTimelineItems(companyId, customerId, blacklistId),
    {
      manual: true,
    },
  );

  useEffect(() => {
    if (open) {
      run();
    }
  }, [open, customerId, blacklistId, run]);

  const events = useMemo(() => {
    const sortedEvent = data?.sort((a, b) =>
      (a?.createdAt || '') > (b?.createdAt || '') ? -1 : 1,
    );

    // 是否是重复申请
    const isRe =
      (data?.filter((item) => item.type === CustomerEventTypeEnum.REDUPLICATE)
        .length || 0) > 0;

    const showReEvents = hasPermission(
      `page:customer-list:${getPoolType(pool)}-pool:show-re-events`,
    );

    if (!isRe || showReEvents) {
      return sortedEvent;
    }

    // 找到最新的一个 RE 的时间
    const reEvent = sortedEvent?.find(
      (item) => item.type === CustomerEventTypeEnum.REDUPLICATE,
    );

    if (reEvent) {
      return sortedEvent?.filter((item) => item.createdAt > reEvent.createdAt);
    }

    return sortedEvent;
  }, [data, pool, hasPermission]);

  // 重复申请的话，要过滤掉没有 showReEvents 权限的 event

  return (
    <Drawer
      className="drawer-box"
      open={open}
      zIndex={99999}
      onClose={onCancel}
      title={customerName}
    >
      <EventTimeline events={events} loading={loading} />
    </Drawer>
  );
};

export default ({
  event,
  customerId,
  customerName,
  highlight,
  pool,
}: {
  event?: EventDTO;
  customerId: I.ID;
  customerName: string;
  highlight?: boolean;
  pool: CustomerPoolEnum;
}) => {
  const { id: companyId } = useCompany();

  const [open, { toggle }] = useBoolean();

  const { data, loading, run } = useRequest(
    () => {
      return customerController.listEvent(companyId, customerId);
    },
    {
      manual: true,
    },
  );

  const events = data?.sort((a, b) =>
    (a?.createdAt || '') > (b?.createdAt || '') ? -1 : 1,
  );

  if (!event?.userId && event?.userId !== 0) {
    return null;
  }

  return (
    <>
      <LastEventDrawer
        open={open}
        onCancel={toggle}
        customerName={customerName}
        customerId={customerId}
        pool={pool}
      />
      <Space direction="vertical">
        <Button
          style={{ color: highlight ? 'red' : undefined, padding: 0 }}
          size="small"
          type="link"
          onClick={() => {
            toggle();
            run();
          }}
        >
          {event?.userName || '系统'}@
          {dayjs(event?.createdAt).format('YYYY-MM-DD HH:mm:ss')}
        </Button>
        <span style={{ whiteSpace: 'pre-line' }}>{event?.comment}</span>
        {/* <span>{event?.remark}</span> */}
      </Space>
    </>
  );
};
